<template>
  <section class="about-admin-page">
    <header class="page-head">
      <div>
        <h1>关于我们</h1>
        <p>维护小程序品牌展示和团队信息，保存后同步到对应主题。</p>
      </div>
      <div class="page-head__actions">
        <label class="theme-select">
          <span>编辑主题</span>
          <select v-model="selectedThemeCode" :disabled="loading || saving" @change="loadThemeDetail">
            <option v-for="theme in themes" :key="theme.themeCode" :value="theme.themeCode">{{ theme.themeName }}{{ theme.isDefault === 1 ? "（默认）" : "" }}</option>
          </select>
        </label>
        <button type="button" class="ghost-button" :disabled="loading || saving" @click="loadAll">刷新</button>
      </div>
    </header>

    <div v-if="message.text" class="about-message" :class="message.type === 'error' ? 'about-message--error' : 'about-message--success'" role="status">
      {{ message.text }}
    </div>

    <div v-if="loading" class="page-state">正在读取关于我们配置...</div>
    <div v-else-if="!selectedThemeCode" class="page-state page-state--empty">还没有可编辑的小程序主题</div>
    <div v-else class="about-workbench">
      <form class="about-editor page-card" @submit.prevent="saveAbout">
        <div class="panel-head">
          <div>
            <h2>品牌资料</h2>
            <p>这些内容会用于小程序“关于我们”页面。</p>
          </div>
          <span class="save-scope">{{ selectedThemeName }}</span>
        </div>

        <div class="form-grid">
          <label class="field">
            <span>小程序名称 *</span>
            <input v-model.trim="draft.appName" maxlength="40" required placeholder="例如：爬仔日记" />
          </label>
          <label class="field">
            <span>版本号</span>
            <input v-model.trim="draft.version" maxlength="24" placeholder="例如：1.0.0" />
          </label>
          <label class="field field--wide">
            <span>Logo 地址</span>
            <input v-model.trim="draft.logoUrl" maxlength="500" placeholder="/uploads/theme-icons/logo.png 或 https://..." />
          </label>
          <label class="field field--wide">
            <span>页面简介</span>
            <textarea v-model.trim="draft.description" maxlength="160" rows="3" placeholder="记录每一次养护，让宠物档案与日常照护更清晰。"></textarea>
          </label>
          <label class="field">
            <span>设计人员</span>
            <input v-model.trim="draft.designer" maxlength="80" placeholder="例如：珠海市每点网络科技有限公司设计组" />
          </label>
          <label class="field">
            <span>程序员</span>
            <input v-model.trim="draft.developer" maxlength="80" placeholder="例如：珠海市每点网络科技有限公司研发组" />
          </label>
          <label class="field field--wide">
            <span>版权信息</span>
            <input v-model.trim="draft.copyright" maxlength="120" placeholder="Copyright © 2026 爬仔日记" />
          </label>
        </div>

        <footer class="editor-actions">
          <span class="editor-tip">Logo 支持线上地址或后端上传路径。</span>
          <button type="submit" class="action-button" :disabled="saving">{{ saving ? "保存中..." : "保存关于我们" }}</button>
        </footer>
      </form>

      <aside class="about-preview page-card" aria-label="小程序关于我们预览">
        <div class="panel-head">
          <div>
            <h2>页面预览</h2>
            <p>按当前配置模拟小程序展示效果。</p>
          </div>
          <span class="preview-badge">MINI APP</span>
        </div>
        <div class="mini-preview">
          <div class="mini-preview__brand">
            <div class="mini-preview__logo" :class="{ 'mini-preview__logo--fallback': !previewLogoUrl }">
              <img v-if="previewLogoUrl" :src="previewLogoUrl" :alt="draft.appName || '小程序 Logo'" @error="previewLogoUrl = ''" />
              <span v-else>爬</span>
            </div>
            <strong>{{ draft.appName || "未设置名称" }}</strong>
            <small>版本 {{ draft.version || "未设置" }}</small>
            <p>{{ draft.description || "还没有填写页面简介" }}</p>
          </div>
          <dl class="mini-preview__facts">
            <div><dt>产品名称</dt><dd>{{ draft.appName || "未设置" }}</dd></div>
            <div><dt>当前版本</dt><dd>{{ draft.version || "未设置" }}</dd></div>
            <div><dt>设计人员</dt><dd>{{ draft.designer || "未填写" }}</dd></div>
            <div><dt>程序员</dt><dd>{{ draft.developer || "未填写" }}</dd></div>
          </dl>
        </div>
      </aside>
    </div>
  </section>
</template>

<script>
import { fetchThemeDetail, fetchThemeList, saveTheme } from "../api/appTheme"
import { resolveBackendAssetUrl } from "../utils/apiEndpoint"

const DEFAULT_LOGO = "https://md.ddcloudrack.com:9999/uploads/theme-icons/20260722165031-445d95a4083f4ffda3ec385ac46f2490.png"

function createDraft() {
  return {
    appName: "爬仔日记",
    logoUrl: DEFAULT_LOGO,
    description: "记录每一次养护，让宠物档案与日常照护更清晰。",
    version: "1.0.0",
    designer: "珠海市每点网络科技有限公司设计组",
    developer: "珠海市每点网络科技有限公司研发组",
    copyright: "Copyright © 2026 爬仔日记"
  }
}

export default {
  name: "AboutView",
  data() {
    return {
      themes: [],
      selectedThemeCode: "",
      selectedTheme: null,
      themeSlots: [],
      draft: createDraft(),
      loading: false,
      saving: false,
      message: { type: "", text: "" }
    }
  },
  computed: {
    selectedThemeName() {
      const theme = this.themes.find(item => item.themeCode === this.selectedThemeCode)
      return theme ? theme.themeName : "未选择主题"
    },
    previewLogoUrl() {
      return this.draft.logoUrl ? resolveBackendAssetUrl(this.draft.logoUrl) : ""
    }
  },
  created() {
    this.loadAll()
  },
  methods: {
    async loadAll() {
      this.loading = true
      this.clearMessage()
      try {
        const res = await fetchThemeList({ clientType: "MINI_APP" })
        this.themes = Array.isArray(res.data) ? res.data : []
        const defaultTheme = this.themes.find(item => item.isDefault === 1) || this.themes[0]
        this.selectedThemeCode = defaultTheme ? defaultTheme.themeCode : ""
        if (this.selectedThemeCode) await this.loadThemeDetail()
      } catch (error) {
        this.showError(error, "关于我们配置加载失败")
      } finally {
        this.loading = false
      }
    },
    async loadThemeDetail() {
      if (!this.selectedThemeCode) return
      this.loading = true
      this.clearMessage()
      try {
        const res = await fetchThemeDetail(this.selectedThemeCode)
        this.selectedTheme = res.data || null
        this.themeSlots = Array.isArray(this.selectedTheme && this.selectedTheme.slots) ? this.selectedTheme.slots : []
        const slot = this.themeSlots.find(item => item.pageCode === "global" && item.slotCode === "brand")
        const config = slot && slot.config ? slot.config : {}
        this.draft = { ...createDraft(), ...this.normalizeConfig(config) }
      } catch (error) {
        this.showError(error, "主题详情加载失败")
      } finally {
        this.loading = false
      }
    },
    normalizeConfig(config) {
      return {
        appName: !config.appName || config.appName === "爬宠管家" ? "爬仔日记" : config.appName,
        logoUrl: config.logoUrl || DEFAULT_LOGO,
        description: config.aboutDescription || config.description || "记录每一次养护，让宠物档案与日常照护更清晰。",
        version: config.aboutVersion || config.version || "1.0.0",
        designer: !config.designer || config.designer === "爬仔日记设计组" ? "珠海市每点网络科技有限公司设计组" : config.designer,
        developer: !config.developer || config.developer === "爬仔日记研发组" ? "珠海市每点网络科技有限公司研发组" : config.developer,
        copyright: config.copyright || `Copyright © 2026 ${config.appName || "爬仔日记"}`
      }
    },
    buildSlots() {
      const payloads = this.themeSlots
        .filter(item => !(item.pageCode === "global" && item.slotCode === "brand"))
        .map(item => ({
          pageCode: item.pageCode,
          slotCode: item.slotCode,
          configType: item.configType || "JSON",
          configJson: JSON.stringify(item.config || {}),
          sortNo: Number(item.sortNo) || 0,
          status: Number(item.status) === 0 ? 0 : 1
        }))
      const original = this.themeSlots.find(item => item.pageCode === "global" && item.slotCode === "brand") || {}
      const brandConfig = {
        ...((original && original.config) || {}),
        appName: this.draft.appName,
        logoUrl: this.draft.logoUrl,
        aboutDescription: this.draft.description,
        aboutVersion: this.draft.version,
        designer: this.draft.designer,
        developer: this.draft.developer,
        copyright: this.draft.copyright
      }
      payloads.push({
        pageCode: "global",
        slotCode: "brand",
        configType: original.configType || "JSON",
        configJson: JSON.stringify(brandConfig),
        sortNo: Number(original.sortNo) || 10,
        status: Number(original.status) === 0 ? 0 : 1
      })
      return payloads
    },
    async saveAbout() {
      if (!this.selectedTheme || !this.draft.appName) {
        this.showMessage("error", "请填写小程序名称")
        return
      }
      this.saving = true
      this.clearMessage()
      try {
        const res = await saveTheme({
          themeCode: this.selectedTheme.themeCode,
          themeName: this.selectedTheme.themeName,
          clientType: this.selectedTheme.clientType || "MINI_APP",
          previewImageUrl: this.selectedTheme.previewImageUrl || "",
          status: Number(this.selectedTheme.status) === 0 ? 0 : 1,
          isDefault: Number(this.selectedTheme.isDefault) === 1 ? 1 : 0,
          versionNo: Number(this.selectedTheme.versionNo) > 0 ? Number(this.selectedTheme.versionNo) : 1,
          remark: this.selectedTheme.remark || "",
          slots: this.buildSlots()
        })
        this.showMessage("success", res.message || "关于我们配置已保存")
        await this.loadThemeDetail()
      } catch (error) {
        this.showError(error, "关于我们配置保存失败")
      } finally {
        this.saving = false
      }
    },
    clearMessage() {
      this.message = { type: "", text: "" }
    },
    showMessage(type, text) {
      this.message = { type, text }
    },
    showError(error, fallback) {
      this.showMessage("error", (error && error.message) || fallback)
    }
  }
}
</script>

<style scoped>
.about-admin-page { max-width: 1440px; margin: 0 auto; }
.page-head { display: flex; align-items: flex-end; justify-content: space-between; gap: 24px; margin-bottom: 20px; }
.page-head h1 { margin: 0; color: #111827; font-size: 28px; }
.page-head p { margin: 8px 0 0; color: #64748b; font-size: 14px; }
.page-head__actions { display: flex; align-items: flex-end; gap: 12px; }
.theme-select { display: flex; min-width: 220px; flex-direction: column; gap: 6px; color: #64748b; font-size: 12px; }
.theme-select select { height: 40px; padding: 0 12px; border: 1px solid #d7deea; border-radius: 12px; color: #334155; background: #fff; }
.about-message { margin-bottom: 16px; padding: 12px 14px; border-radius: 12px; font-size: 13px; }
.about-message--success { background: #ecfdf3; color: #166534; }
.about-message--error { background: #fff1f2; color: #be123c; }
.about-workbench { display: grid; grid-template-columns: minmax(0, 1.35fr) minmax(320px, .8fr); gap: 20px; align-items: start; }
.about-editor, .about-preview { min-width: 0; }
.panel-head { display: flex; align-items: flex-start; justify-content: space-between; gap: 16px; margin-bottom: 20px; }
.panel-head h2 { margin: 0; color: #111827; font-size: 19px; }
.panel-head p { margin: 6px 0 0; color: #64748b; font-size: 13px; }
.save-scope, .preview-badge { padding: 5px 9px; border-radius: 8px; color: #2563eb; background: #eff6ff; font-size: 11px; white-space: nowrap; }
.form-grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 16px; }
.field { display: flex; min-width: 0; flex-direction: column; gap: 7px; color: #475569; font-size: 13px; font-weight: 600; }
.field--wide { grid-column: 1 / -1; }
.field input, .field textarea { width: 100%; padding: 11px 12px; border: 1px solid #dbe3ee; border-radius: 10px; outline: none; color: #111827; background: #fff; font-size: 14px; font-weight: 400; }
.field input:focus, .field textarea:focus { border-color: #93c5fd; box-shadow: 0 0 0 3px rgba(37, 99, 235, .1); }
.field textarea { resize: vertical; line-height: 1.6; }
.editor-actions { display: flex; align-items: center; justify-content: space-between; gap: 16px; margin-top: 22px; padding-top: 18px; border-top: 1px solid #eef2f7; }
.editor-tip { color: #94a3b8; font-size: 12px; }
.mini-preview { padding: 28px 22px 22px; border: 1px solid #e7edf5; border-radius: 16px; background: #f8fafc; }
.mini-preview__brand { display: flex; flex-direction: column; align-items: center; text-align: center; }
.mini-preview__logo { display: flex; width: 84px; height: 84px; align-items: center; justify-content: center; overflow: hidden; border: 1px solid #e2e8f0; border-radius: 10px; background: #fff; }
.mini-preview__logo img { width: 76px; height: 76px; object-fit: contain; }
.mini-preview__logo span { color: #fff; font-size: 34px; font-weight: 700; }
.mini-preview__logo--fallback { background: #171717; }
.mini-preview__brand strong { margin-top: 14px; color: #171717; font-size: 20px; }
.mini-preview__brand small { margin-top: 5px; color: #94a3b8; font-size: 12px; }
.mini-preview__brand p { max-width: 320px; margin: 16px 0 0; color: #64748b; font-size: 13px; line-height: 1.7; }
.mini-preview__facts { margin: 24px 0 0; padding: 0 14px; border: 1px solid #e7edf5; border-radius: 10px; background: #fff; }
.mini-preview__facts div { display: flex; align-items: center; justify-content: space-between; gap: 12px; min-height: 44px; border-bottom: 1px solid #eef2f7; }
.mini-preview__facts div:last-child { border-bottom: 0; }
.mini-preview__facts dt { color: #475569; font-size: 12px; }
.mini-preview__facts dd { margin: 0; color: #64748b; font-size: 12px; text-align: right; }
@media (max-width: 900px) { .about-workbench { grid-template-columns: 1fr; } }
@media (max-width: 640px) { .page-head { align-items: stretch; flex-direction: column; } .page-head__actions { align-items: stretch; } .theme-select { flex: 1; } .form-grid { grid-template-columns: 1fr; } .field--wide { grid-column: auto; } .editor-actions { align-items: stretch; flex-direction: column; } }
</style>
